<template>
  <div>
    <div>
      <v-breadcrumbs
        class="px-0"
        :items="[
          { text: $t('guidance.title'), href: '/guidance-plan/list' },
          { text: $t('guidance.detail') },
        ]"
      ></v-breadcrumbs>
    </div>

    <div class="d-flex align-center">
      <h2>{{ $t('guidance.detail') }}</h2>
      <v-spacer />
      <v-btn
        @click="downloadGuidancePlan"
        class="mx-2"
        :disabled="loading"
        color="primary"
        variant="outlined"
      >
        <v-icon start>mdi-tray-arrow-down</v-icon>
        {{ $t('common.download') }}
      </v-btn>
      <v-btn
        @click="updateApproval"
        :disabled="loading"
        color="primary"
        variant="flat"
      >
        {{ $t('guidance.approve') }}
      </v-btn>
    </div>

    <!-- plan detail table -->
    <div class="mt-6">
      <v-progress-linear indeterminate v-if="loading" />
      <v-alert type="info" variant="outlined">
        <v-table density="compact" v-if="planResponse">
          <template v-slot:default>
            <thead>
              <tr>
                <th></th>
                <th>{{ $t('guidance.name') }}</th>
                <th>{{ $t('guidance.type') }}</th>
                <th>{{ $t('guidance.year') }}</th>
                <th>{{ $t('guidance.month') }}</th>
                <th>{{ $t('guidance.week') }}</th>
                <th>{{ $t('guidance.createdAt') }}</th>
                <th>{{ $t('guidance.updatedAt') }}</th>
              </tr>
            </thead>
            <tbody v-if="planResponse">
              <tr>
                <td>
                  <h5>{{ $t('guidance.title') }}</h5>
                </td>
                <td>{{ planResponse.name }}</td>
                <td>{{ getPlanTypeText(planResponse.type) }}</td>
                <td>{{ planResponse.year }}</td>
                <td>{{ planResponse.month || "" }}</td>
                <td>{{ planResponse.week || "" }}</td>
                <td>{{ formatDate(planResponse.createdAt) }}</td>
                <td>{{ formatDate(planResponse.updatedAt) }}</td>
              </tr>
              <tr v-if="planResponse.parentPlan">
                <td>
                  <h5>{{ $t('guidance.parentPlan') }}</h5>
                </td>
                <td>{{ planResponse.parentPlan.name }}</td>
                <td>{{ getPlanTypeText(planResponse.parentPlan.type) }}</td>
                <td>{{ planResponse.parentPlan.year }}</td>
                <td>{{ planResponse.parentPlan.month || "" }}</td>
                <td>{{ planResponse.parentPlan.week || "" }}</td>
                <td>{{ formatDate(planResponse.parentPlan.createdAt) }}</td>
                <td>{{ formatDate(planResponse.parentPlan.updatedAt) }}</td>
              </tr>
            </tbody>
          </template>
        </v-table>
      </v-alert>
      <!-- <v-divider /> -->
    </div>

    <!-- approved by -->
    <div class="mt-6">
      <v-row v-if="planResponse">
        <v-spacer />
        <v-col cols="2">
          <v-text-field
            :label="$t('guidance.director')"
            :value="
              planResponse.users.director
                ? `${planResponse.users.director.lastName}${planResponse.users.director.firstName}`
                : ''
            "
            variant="outlined"
            density="compact"
            readonly
            hide-details=""
          ></v-text-field>
        </v-col>
        <v-col cols="2">
          <v-text-field
            :value="
              planResponse.users.chief
                ? `${planResponse.users.chief.lastName}${planResponse.users.chief.firstName}`
                : ''
            "
            :label="$t('guidance.chief')"
            density="compact"
            variant="outlined"
            readonly
            hide-details=""
          ></v-text-field>
        </v-col>
        <v-col cols="2">
          <v-text-field
            :label="$t('guidance.personInCharge')"
            :value="
              planResponse.users.applicant
                ? `${planResponse.users.applicant.lastName}${planResponse.users.applicant.firstName}`
                : ''
            "
            variant="outlined"
            density="compact"
            readonly
            hide-details=""
          ></v-text-field>
        </v-col>
      </v-row>
    </div>

    <!-- itemList -->
    <div class="mt-6">
      <v-sheet v-if="planResponse" elevation="2" class="pa-2">
        <v-card
          v-for="(item, i) in planResponse.itemList"
          :key="i"
          flat
          class="my-2 mx-8"
        >
          <v-container>
            <!-- heading -->
            <div v-if="item.item.type == 'heading'">
              <v-row>
                <v-col cols="12">
                  <h2>{{ item.item.name }}</h2>
                </v-col>
              </v-row>
            </div>
            <!-- text -->
            <div v-else-if="item.item.type == 'text'">
              <table class="table" border="1">
                <tbody>
                  <tr>
                    <th class="w-50per">{{ item.item.name }}</th>
                    <td class="w-50per">
                      <v-textarea
                        v-model="item.item.body"
                        filled
                        density="compact"
                        hide-details=""
                        auto-grow
                      ></v-textarea>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <!-- childText -->
            <div v-else-if="item.item.type == 'childText'">
              <h3 class="my-2">{{ item.item.name }}</h3>
              <table class="table" border="1">
                <tbody>
                  <tr
                    v-for="childItem in item.item.childTextList"
                    :key="childItem.childId"
                  >
                    <th class="w-50per">
                      {{
                        `${childItem.childName.lastName}${childItem.childName.firstName}`
                      }}
                    </th>
                    <td class="w-50per">
                      <v-textarea
                        v-model="childItem.body"
                        filled
                        density="compact"
                        hide-details=""
                        auto-grow
                      ></v-textarea>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- grid -->
            <table class="table" border="1" v-if="item.item.type == 'grid'">
              <thead>
                <tr>
                  <th></th>
                  <th
                    v-for="(colsItem, c) in item.item.grid.header.cols"
                    :key="c"
                  >
                    {{ colsItem }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(rowsItem, r) in item.item.grid.header.rows"
                  :key="r"
                >
                  <th nowrap class="px-2">{{ rowsItem }}</th>
                  <td
                    v-for="col in item.item.grid.header.cols.length"
                    :key="col"
                  >
                    <v-textarea
                      v-model="item.item.grid.values[r][col]"
                      filled
                      density="compact"
                      hide-details=""
                      auto-grow
                    ></v-textarea>
                  </td>
                </tr>
              </tbody>
            </table>
          </v-container>
        </v-card>
      </v-sheet>
    </div>

    <v-row class="mt-6" align="center" justify="center">
      <v-col cols="3">
        <v-btn
          @click="updatePlan()"
          :disabled="loading"
          color="primary"
          variant="flat"
          block
        >
          {{ $t('common.save') }}
        </v-btn>
      </v-col>
    </v-row>
  </div>
</template>

,
<script lang="ts">
import { defineComponent } from "vue";
import dayjs from "dayjs";
import ApiGuidance from "@/api/ApiGuidance";
import LocalDataService from "@/service/LocalDataService";
import { meModule } from "@/store/dataModules/meModule";
import {
  MeNurserySchoolResponse,
  MeResponse,
  NurseryGuidancePlanResponse,
} from "chaild-api/lib";
import { GuidancePlanTypeItems } from "@/constants/guidance";

export default defineComponent({
  name: "GuidancePlanDetail",

  async mounted() {
    const planIdParam = this.$route.params.id;
    const planIdStr = typeof planIdParam === "string" ? planIdParam : planIdParam?.[0];
    if (planIdStr) {
      this.planId = planIdStr;
    }
    await this.getPlan();
  },

  data() {
    return {
      loading: false,
      planId: "",
      planTypeItems: GuidancePlanTypeItems,
      planResponse: null as null | NurseryGuidancePlanResponse,
    };
  },

  computed: {
    me: function (): MeResponse | null {
      return meModule.me;
    },
    currentSchool(): MeNurserySchoolResponse | null {
      if (this.me) {
        const nurseryId = LocalDataService.getNurseryId();
        return (
          this.me.nurserySchools.find((s) => s.nurseryId === nurseryId) || null
        );
      } else {
        return null;
      }
    },
    isDirector(): boolean {
      return !!(this.currentSchool && this.currentSchool.isDirector);
    },
    isChief(): boolean {
      return !!(this.currentSchool && this.currentSchool.isChief);
    },
  },

  methods: {
    async getPlan() {
      this.loading = true;
      const nurseryId = LocalDataService.getNurseryId();
      if (nurseryId) {
        const response = await ApiGuidance.getPlan({
          nurseryId,
          planId: this.planId,
        }).catch(() => (this.loading = false));
        if (response) {
          this.planResponse = response;
        }
      }
      this.loading = false;
    },
    async updatePlan() {
      this.loading = true;
      const nurseryId = LocalDataService.getNurseryId();
      if (nurseryId && this.planResponse) {
        const _itemListBody = this.planResponse.itemList.map((item) => {
          return {
            id: item.item.id,
            body: item.item.body || null,
            childTextList: item.item.childTextList
              ? item.item.childTextList.map((c) => {
                  return {
                    childId: c.childId,
                    body: c.body || null,
                  };
                })
              : undefined,
            grid: item.item.grid || null,
          };
        });
        const response = await ApiGuidance.updatePlan({
          nurseryId,
          planId: this.planResponse.id,
          itemList: _itemListBody,
        }).catch(() => (this.loading = false));
        if (response) {
          alert(this.$t('settings.savedSuccessfully'));
          await this.getPlan();
        }
      }
      this.loading = false;
    },
    async updateApproval() {
      const answer = confirm(this.$t('guidance.approve') + '?');
      if (answer) {
        this.loading = true;
        const nurseryId = LocalDataService.getNurseryId();
        if (nurseryId && this.planResponse && this.currentSchool) {
          const _input = {
            applicationId: undefined as undefined | number,
            chiefId: undefined as undefined | number,
            directorId: undefined as undefined | number,
          };
          if (this.isDirector) {
            _input.directorId = this.currentSchool.userId;
          } else if (this.isChief) {
            _input.chiefId = this.currentSchool.userId;
          }
          const response = await ApiGuidance.updatePlanApproval({
            planId: this.planResponse.id,
            nurseryId,
            ..._input,
          }).catch(() => (this.loading = false));
          if (response) {
            alert(this.$t('admin.updated'));
            await this.getPlan();
          }
        }
        this.loading = false;
      }
    },
    async downloadGuidancePlan() {
      if (this.planId) {
        this.loading = true;
        const response = await ApiGuidance.exportPlan({
          planId: this.planId,
        }).catch(() => (this.loading = false));
        if (response) {
          window.open(response.preSignedDownloadUrl, "_blank");
        }
      }
      this.loading = false;
    },
    getPlanTypeText(value: string): string {
      const item = this.planTypeItems.find((p) => p.value == value);
      return item?.text || "";
    },
    formatDate(value: string): string {
      if (!value) {
        return "";
      }
      return dayjs(value).format("DD/MM/YYYY");
    },
  },
});
</script>

<style scoped>
.table {
  width: 100%;
  border-collapse: collapse;
}

.w-50per {
  width: 50%;
}
</style>
